// Guide §3 "সময়" + Bible DEC-29: all business dates / reports / jobs run in Asia/Dhaka (UTC+6, no DST),
// day boundary = midnight Dhaka time. Server clock stays UTC.
export const BUSINESS_TZ = 'Asia/Dhaka';
/** Bangladesh has no DST: fixed offset +06:00. Used for fast arithmetic. */
export const BD_OFFSET_MINUTES = 6 * 60;
export const MS_DAY = 86_400_000;

const bizFmt = new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TZ });

/** 'YYYY-MM-DD' of the business day in Asia/Dhaka for a given instant. */
export const bizDate = (ts: Date | string | number): string => bizFmt.format(new Date(ts));

/** Date-only ('YYYY-MM-DD') → Date at 00:00 UTC (what Prisma `@db.Date` values round-trip as). */
export const dateOnlyToUtc = (d: string): Date => new Date(`${d}T00:00:00.000Z`);

/** Instant at which the given business day starts (00:00 Asia/Dhaka). */
export const startOfBizDay = (d: string): Date => new Date(dateOnlyToUtc(d).getTime() - BD_OFFSET_MINUTES * 60_000);

/** Add N calendar days to a 'YYYY-MM-DD' string. */
export function addDaysToDate(d: string, n: number): string {
  const t = dateOnlyToUtc(d).getTime() + n * MS_DAY;
  return new Date(t).toISOString().slice(0, 10);
}

export const addDays = (d: Date, n: number): Date => new Date(d.getTime() + n * MS_DAY);
export const addMinutes = (d: Date, n: number): Date => new Date(d.getTime() + n * 60_000);
export const addHours = (d: Date, n: number): Date => new Date(d.getTime() + n * 3_600_000);

/** Wall-clock parts in Asia/Dhaka. */
export function bizParts(ts: Date | string | number): {
  y: number;
  m: number;
  d: number;
  hh: number;
  mm: number;
  dow: number; // 0 = Sunday
  date: string;
} {
  const t = new Date(ts).getTime() + BD_OFFSET_MINUTES * 60_000;
  const x = new Date(t);
  return {
    y: x.getUTCFullYear(),
    m: x.getUTCMonth() + 1,
    d: x.getUTCDate(),
    hh: x.getUTCHours(),
    mm: x.getUTCMinutes(),
    dow: x.getUTCDay(),
    date: x.toISOString().slice(0, 10),
  };
}

/** Instant for 'YYYY-MM-DD' + 'HH:MM' in Asia/Dhaka. */
export function bizAt(date: string, hhmm: string): Date {
  const [h, m] = hhmm.split(':').map(Number);
  return new Date(startOfBizDay(date).getTime() + ((h ?? 0) * 60 + (m ?? 0)) * 60_000);
}

/** Whole days between two 'YYYY-MM-DD' strings (b − a). */
export const diffDays = (a: string, b: string): number =>
  Math.round((dateOnlyToUtc(b).getTime() - dateOnlyToUtc(a).getTime()) / MS_DAY);

/** Week start (Bible 2.9: default Saturday, configurable). weekStart: 0=Sun … 6=Sat. */
export function weekRange(date: string, weekStart = 6): { from: string; to: string } {
  const dow = new Date(`${date}T00:00:00Z`).getUTCDay();
  const back = (dow - weekStart + 7) % 7;
  const from = addDaysToDate(date, -back);
  return { from, to: addDaysToDate(from, 6) };
}

export function monthRange(date: string): { from: string; to: string } {
  const [y, m] = date.split('-').map(Number) as [number, number];
  const from = `${y}-${String(m).padStart(2, '0')}-01`;
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from, to: `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}` };
}

/** Quiet hours for push (Bible 5.6): 22:00–08:00 Asia/Dhaka. */
export const QUIET_START_HOUR = 22;
export const QUIET_END_HOUR = 8;
export function isQuietHour(ts: Date | string | number): boolean {
  const { hh } = bizParts(ts);
  return hh >= QUIET_START_HOUR || hh < QUIET_END_HOUR;
}
/** Next 08:00 Asia/Dhaka at or after ts (when ts is inside quiet hours; otherwise ts). */
export function releaseQuietAt(ts: Date): Date {
  if (!isQuietHour(ts)) return ts;
  const p = bizParts(ts);
  const day = p.hh >= QUIET_START_HOUR ? addDaysToDate(p.date, 1) : p.date;
  return bizAt(day, '08:00');
}

/** Guide §3 "ঘড়ি": clientTs more than 2 minutes in the future is clamped to receivedAt. */
export const MAX_CLIENT_FUTURE_MS = 2 * 60_000;
export function clampClientTs(clientTs: Date, receivedAt: Date): { ts: Date; clamped: boolean } {
  if (clientTs.getTime() - receivedAt.getTime() > MAX_CLIENT_FUTURE_MS) return { ts: receivedAt, clamped: true };
  return { ts: clientTs, clamped: false };
}

/** Working hours (Bible 16.5): daily 09:00–21:00 Asia/Dhaka = 12 working hours per day. */
export interface WorkingWindow {
  start: string; // 'HH:MM'
  end: string;
}
export const DEFAULT_WORKING_WINDOW: WorkingWindow = { start: '09:00', end: '21:00' };

/** Add N working hours to `from`, skipping the closed window (Guide §16.4). */
export function addWorkingHours(from: Date, hours: number, win: WorkingWindow = DEFAULT_WORKING_WINDOW): Date {
  const [sh, sm] = win.start.split(':').map(Number) as [number, number];
  const [eh, em] = win.end.split(':').map(Number) as [number, number];
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  let remaining = Math.round(hours * 60);
  let cur = from;
  // guard against pathological loops
  for (let guard = 0; guard < 400 && remaining > 0; guard++) {
    const p = bizParts(cur);
    const minOfDay = p.hh * 60 + p.mm;
    if (minOfDay < startMin) {
      cur = bizAt(p.date, win.start);
      continue;
    }
    if (minOfDay >= endMin) {
      cur = bizAt(addDaysToDate(p.date, 1), win.start);
      continue;
    }
    const available = endMin - minOfDay;
    if (remaining <= available) return new Date(cur.getTime() + remaining * 60_000);
    remaining -= available;
    cur = bizAt(addDaysToDate(p.date, 1), win.start);
  }
  return cur;
}

/** Is `ts` inside support working hours? */
export function isWithinWorkingHours(ts: Date, win: WorkingWindow = DEFAULT_WORKING_WINDOW): boolean {
  const p = bizParts(ts);
  const [sh, sm] = win.start.split(':').map(Number) as [number, number];
  const [eh, em] = win.end.split(':').map(Number) as [number, number];
  const m = p.hh * 60 + p.mm;
  return m >= sh * 60 + sm && m < eh * 60 + em;
}

/** Same day-of-month `n` months later (clamped to the month's last day: 31 Jan + 1 month = 28/29 Feb). Used by monthly retainers. */
export function addMonthsToDate(d: string, n: number): string {
  const [y, m, day] = d.split('-').map(Number) as [number, number, number];
  const idx = y * 12 + (m - 1) + n;
  const ny = Math.floor(idx / 12);
  const nm = (idx % 12) + 1;
  const last = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
  return `${String(ny).padStart(4, '0')}-${String(nm).padStart(2, '0')}-${String(Math.min(day, last)).padStart(2, '0')}`;
}
